import { RgbaColor, colord, extend } from 'colord';
import { useEffect, useState } from 'react';
import { RgbaColorPicker } from 'react-colorful';
import styled from 'styled-components';

import namesPlugin from 'colord/plugins/names';
import { parseGradientString } from '../../../../functions/utils/parseGradientString';
import { CommonInput } from '../CommonInput';
import { UnitInput } from '../UnitInput';
import { SwitchInput } from '../switchInput/SwitchInput';
import { AdditionalInputs } from './AdditionalInputs';
import { ColorSwatches } from './ColorSwatches';

extend([namesPlugin]);

export const MultiColorPicker = ({
  value,
  onChange,

  extended,
  setExtended,
}: {
  value?: string;
  onChange: (newValue: string | undefined) => void;

  extended?: boolean;
  setExtended?: (newValue: boolean) => void;
}) => {
  const [isExtended, setIsExtended] = useState(extended || false);
  const parsedGradient = parseGradientString(value);
  if (parsedGradient.gradientType === null) {
    parsedGradient.colorList = [
      {
        rgba: value ? (colord(value).toRgb() as RgbaColor) : undefined,
        stop: null,
      },
    ];
  }
  const [colorList, setColorList] = useState(parsedGradient.colorList);
  const [selectedID, setSelectedID] = useState(0);
  const [gradientType, setGradientType] = useState(
    parsedGradient.gradientType || 'linear-gradient'
  );
  const [gradientDirection, setGradientDirection] = useState(
    parsedGradient.gradientDirection || '90deg'
  );

  const updateColor = (index: number, newColor: RgbaColor) => {
    const colors = [...colorList];
    colors[index].rgba = newColor;

    setColorList(colors);
  };

  useEffect(() => {
    if (colorList.length === 1) {
      const color = colorList[0].rgba;
      color && onChange(colord(color).toHex());
      return;
    }

    const colorsString = colorList
      .map((colorItem) => colorItem.rgba && colord(colorItem.rgba).toHex())
      .join(', ');

    const directionString =
      gradientType === 'linear-gradient' ? `${gradientDirection}, ` : '';

    const gradientString = `${gradientType}(${directionString}${colorsString})`;

    onChange(gradientString);
  }, [colorList, gradientType, gradientDirection]);

  return (
    <Container>
      <ExtendButton
        $isOpen={isExtended}
        onClick={() => {
          setIsExtended(!isExtended);
          setExtended && setExtended(!isExtended);
        }}
      >
        <i className="icon-down-open-mini" />
      </ExtendButton>

      {isExtended && (
        <ExtendContainer>
          <ColorSwatches
            handleColorClick={(newColor: RgbaColor) => {
              updateColor(selectedID, newColor);
            }}
          />
        </ExtendContainer>
      )}

      <RgbaColorPicker
        color={colorList[selectedID].rgba}
        onChange={(newValue) => updateColor(selectedID, newValue)}
      />
      <AdditionalInputs
        rgba={colorList[selectedID].rgba}
        updateColor={(newColor) => updateColor(selectedID, newColor)}
      />

      {colorList.length > 1 && (
        <>
          <GradientSettingsInputs>
            <GradientType
              onClick={() => {
                gradientType === 'linear-gradient'
                  ? setGradientType('radial-gradient')
                  : setGradientType('linear-gradient');
              }}
            >
              <GradientTypeLabel $active={gradientType === 'linear-gradient'}>
                Linear
              </GradientTypeLabel>
              <SwitchInput
                id={`gradient-type-toggle`}
                value={gradientType === 'radial-gradient'}
                onChange={() => {}}
                nocolor
              />
              <GradientTypeLabel $active={gradientType === 'radial-gradient'}>
                Radial
              </GradientTypeLabel>
            </GradientType>

            {gradientType === 'linear-gradient' && (
              <UnitInput
                id={`gradient-direction-input`}
                value={gradientDirection}
                units={['deg']}
                defaultUnit="deg"
                onChange={(newValue) => setGradientDirection(String(newValue))}
              />
            )}
          </GradientSettingsInputs>
          <MultiColorList>
            {colorList.map((colorItem, index) => (
              <MultiColorItem
                key={`colorItem-${index}`}
                $active={selectedID === index}
              >
                <MultiColorPreview onClick={() => setSelectedID(index)}>
                  <Checkboard
                    key={
                      colorItem.rgba
                        ? colord(colorItem.rgba).toHex()
                        : undefined
                    }
                  />
                  <ColorFill
                    $color={
                      colorItem.rgba
                        ? colord(colorItem.rgba).toHex()
                        : undefined
                    }
                    onClick={() => {}}
                  />
                </MultiColorPreview>

                <CommonInput>
                  <input
                    id={`multicolor-input-${index}`}
                    style={{ backgroundColor: 'var(--panel-bg-color' }}
                    type="text"
                    placeholder={
                      colorItem.rgba ? colord(colorItem.rgba).toHex() : ''
                    }
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const inputElement = document.getElementById(
                          `multicolor-input-${index}`
                        );
                        inputElement?.blur();
                      }
                    }}
                    onFocus={(e) => {
                      setSelectedID(index);
                    }}
                    onBlur={(e) => {
                      const value = e.target.value;
                      const rgba = value
                        ? (colord(value).toRgb() as RgbaColor)
                        : undefined;

                      if (rgba) {
                        updateColor(index, rgba);
                      }
                      e.target.value = '';
                    }}
                  />
                </CommonInput>

                <RemoveColorButton
                  onClick={() => {
                    setSelectedID(0);
                    setColorList((prev) =>
                      prev.filter((colorItem, i) => i !== index)
                    );
                  }}
                >
                  <i className="icon-minus" />
                </RemoveColorButton>
              </MultiColorItem>
            ))}
          </MultiColorList>
        </>
      )}

      <AddColorButton
        onClick={() => {
          if (colorList[0]?.rgba === undefined) {
            updateColor(0, { r: 255, g: 255, b: 255, a: 1 });
          }
          setColorList((prev) => [
            ...prev,
            { rgba: { r: 0, g: 0, b: 0, a: 1 }, stop: null },
          ]);
          setSelectedID(colorList.length);
          parsedGradient.gradientType = 'linear-gradient';
        }}
      >
        <i className="icon-plus" />
      </AddColorButton>
    </Container>
  );
};

const Container = styled.div`
  width: 200px;
  padding: 5px;
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 5px 15px;

  label {
    padding-left: 3px;
    padding-right: 8px;
  }

  color: var(--text-color);
  font-family: 'Inter';
  font-size: 12px;
`;

const ExtendButton = styled.button<{ $isOpen: boolean }>`
  cursor: pointer;
  width: 100%;
  height: 16px;
  margin-bottom: 10px;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: var(--panel-bg-color-secondary);
  border-radius: 8px;

  &:hover {
    background-color: var(--panel-bg-color-hover);
  }

  & > i {
    transform: rotate(90deg);
    transition: transform 0.2s ease;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    & > i {
      transform: rotate(-90deg);
    }
  `}
`;

const ExtendContainer = styled.div`
  user-select: none;
  position: absolute;
  top: 0;
  left: -100%;

  width: 200px;
  padding: 5px;
  border-radius: 10px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 5px 15px;
  background-color: var(--panel-bg-color-secondary);

  label {
    padding-left: 3px;
    padding-right: 8px;
  }

  color: var(--text-color);
  font-family: 'Inter';
  font-size: 12px;
`;

const Checkboard = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;

  background: url('data:image/svg+xml;utf8,<svg preserveAspectRatio="none"  viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg"><rect x="0" y="0" width="5" height="5" fill="grey" /><rect x="5" y="5" width="5" height="5" fill="grey" /><rect x="5" y="0" width="5" height="5" fill="white" /><rect x="0" y="5" width="5" height="5" fill="white" /></svg>')
    0 0/15px 15px round;
`;

const ColorFill = styled.div<{ $color?: string }>`
  box-sizing: border-box;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${({ $color }) => $color && `background-color: ${$color};`}
`;

const MultiColorList = styled.div`
  width: 100%;
  margin-top: 10px;
`;

const MultiColorItem = styled.div<{ $active?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;

  padding: 5px;
  border-radius: 16px;

  &:hover {
    background-color: var(--panel-bg-color-hover);
  }

  ${({ $active }) =>
    $active && `background-color: var(--panel-bg-color-hover);`}
`;

const MultiColorPreview = styled.div`
  cursor: pointer;
  position: relative;
  box-sizing: border-box;
  position: relative;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  overflow: hidden;

  margin-right: 10px;
`;

const AddColorButton = styled.button`
  cursor: pointer;
  width: 100%;
  height: 16px;
  margin-top: 10px;

  display: flex;
  justify-content: center;
  align-items: center;

  background-color: var(--panel-bg-color-secondary);
  border-radius: 8px;

  &:hover {
    background-color: var(--panel-bg-color-hover);
  }

  & > i {
    transform: rotate(90deg);
    transition: transform 0.2s ease;
  }
`;

const RemoveColorButton = styled.button`
  cursor: pointer;
  padding: 2px;
  border-radius: 5px;

  &:hover {
    background-color: var(--panel-bg-color);
  }
`;

const GradientSettingsInputs = styled.div`
  display: flex;
  align-items: center;
  margin-top: 10px;
`;

const GradientType = styled.div`
  cursor: pointer;
  user-select: none;
  width: fit-content;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 3px 10px;

  gap: 5px;
  background-color: var(--panel-bg-color-secondary);
  border-radius: 10px;

  &:hover {
    background-color: var(--panel-bg-color-hover);
  }

  & > * {
    pointer-events: none;
  }
`;

const GradientTypeLabel = styled.div<{ $active?: boolean }>`
  color: var(--text-color-secondary);
  font-size: 12px;

  ${({ $active }) => $active && `color: var(--accent-color);`}
`;
